<script setup lang="ts">
import { onKeyStroke, useEventListener } from "@vueuse/core";
import { computed, inject, onMounted, onUnmounted, watch } from "vue";
import { routerKey } from "vue-router";
import { useDialogStack } from "../../composables/Dialog/useDialogStack";
import DialogLayerProvider from "./DialogLayerProvider.vue";
import { isDirtyNavigationSuppressed, requestDirtyConfirm } from "./dialogStack";
import VDialog from "./VDialog.vue";

const stack = useDialogStack();
const router = inject(routerKey, null); // Router is optional (tests, nested hosts).

const hostLayers = computed(() => stack.layers.filter(layer => layer.source === "host" && layer.component));
const showMask = computed(() => stack.maskLayer.value !== null);

function handleMaskClick() {
  const top = stack.top.value;
  if (!top || !top.dismissible) {
    return;
  }
  void stack.pop(top.instanceId);
}

watch(() => stack.topZIndex.value, (zIndex) => {
  document.documentElement.style.setProperty(
    "--dialog-top-z",
    String(stack.layers.length > 0 ? zIndex : 10000),
  );
}, { immediate: true });

onKeyStroke("Escape", () => {
  const top = stack.top.value;
  if (!top || !top.dismissible) {
    return;
  }
  void stack.pop(top.instanceId);
});

useEventListener(window, "beforeunload", (event: BeforeUnloadEvent) => {
  if (stack.anyDirty()) {
    event.preventDefault();
  }
});

let removeGuard: (() => void) | undefined;

onMounted(() => {
  removeGuard = router?.beforeEach(async (to, from) => {
    if (to.fullPath === from.fullPath) {
      return true;
    }
    if (isDirtyNavigationSuppressed() || !stack.anyDirty()) {
      return true;
    }
    return requestDirtyConfirm();
  });
});

onUnmounted(() => {
  removeGuard?.();
});

function layerListeners(layer: typeof hostLayers.value[number]) {
  const listeners = layer.listeners ?? {};
  return {
    ...listeners,
    confirm: (...args: unknown[]) => {
      if (typeof listeners.confirm === "function") {
        listeners.confirm(...args);
        return;
      }
      if (typeof layer.props.onConfirm === "function") {
        (layer.props.onConfirm as (...inner: unknown[]) => void)(...args);
      }
    },
    cancel: (...args: unknown[]) => {
      if (typeof listeners.cancel === "function") {
        listeners.cancel(...args);
        return;
      }
      if (typeof layer.props.onCancel === "function") {
        (layer.props.onCancel as (...inner: unknown[]) => void)(...args);
        return;
      }
      void stack.pop(layer.instanceId);
    },
    close: (...args: unknown[]) => {
      listeners.close?.(...args);
      void stack.pop(layer.instanceId);
    },
    success: (...args: unknown[]) => {
      listeners.success?.(...args);
      void stack.popAfterSave(layer.instanceId);
    },
  };
}
</script>

<template>
  <Teleport to="body">
    <Transition name="fade">
      <div
        v-if="showMask"
        :key="stack.maskZIndex.value"
        class="mask"
        :style="{ zIndex: stack.maskZIndex.value }"
        @click="handleMaskClick"
        @mousedown.stop
      />
    </Transition>
  </Teleport>
  <DialogLayerProvider
    v-for="layer in hostLayers"
    :key="layer.instanceId"
    :layer="layer"
  >
    <VDialog
      :id="layer.id"
      :open="true"
      hosted
      :instance-id="layer.instanceId"
      :size="layer.size"
      :width="layer.width"
      :height="layer.height"
      :variant="layer.variant"
      :priority="layer.priority"
      :headless="layer.headless"
      :paddingless="layer.paddingless"
      :header="layer.header"
      :footer="layer.footer"
      :mask="layer.mask"
      :closable="layer.dismissible"
      :alert="layer.alert"
      :is-dirty="() => layer.dirtyFlag || Boolean(layer.dirtyFn?.())"
      @close="stack.pop(layer.instanceId)"
    >
      <component
        :is="layer.component"
        v-bind="layer.props"
        v-on="layerListeners(layer)"
      />
    </VDialog>
  </DialogLayerProvider>
</template>

<style scoped lang="scss">
.mask {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.14s ease;
}

// The outgoing mask sits below the dialog it used to dim, so it must not catch
// clicks meant for that dialog while it fades away.
.fade-leave-active {
  pointer-events: none;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
